import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { toast } from "sonner";

export default function HealthScoreCalculator({ investor, activities = [], tasks = [], onScoreUpdated }) {
  const [calculating, setCalculating] = useState(false);
  const queryClient = useQueryClient();

  const calculateScore = async () => {
    setCalculating(true);
    try {
      // Gather data for analysis
      const recentActivities = activities.slice(0, 20);
      const daysSinceLastContact = investor.last_contact_date 
        ? Math.floor((new Date() - new Date(investor.last_contact_date)) / (1000 * 60 * 60 * 24))
        : null;

      const activitiesText = recentActivities
        .map(a => `${a.type}: ${a.title} - ${a.description || ""}`)
        .join("\n");

      const investorTasks = tasks.filter(t => t.related_to_id === investor.id);
      const completedTasks = investorTasks.filter(t => t.status === "completed").length;
      const totalTasks = investorTasks.length;
      const taskCompletionRate = totalTasks > 0 ? (completedTasks / totalTasks * 100) : 0;

      // Deal stage progress (1-7)
      const dealStageMap = {
        initial_contact: 1,
        qualification: 2,
        pitch: 3,
        due_diligence: 4,
        negotiation: 5,
        closing: 6,
        closed_won: 7,
        closed_lost: 0,
      };
      const dealStageProgress = dealStageMap[investor.deal_stage] || 1;

      const result = await base44.integrations.Core.InvokeLLM({
        prompt: `Du er en CRM-analytiker som beregner relasjonshelse-score for en investor.

Analyser følgende data og beregn en helhetlig relasjonshelse-score (0-100):

**Investor:** ${investor.name}
**Status:** ${investor.status}
**Deal Stage:** ${investor.deal_stage} (Progress: ${dealStageProgress}/7)
**Dager siden siste kontakt:** ${daysSinceLastContact !== null ? daysSinceLastContact : "Ukjent"}
**Antall aktiviteter:** ${activities.length}
**Oppgavefremdrift:** ${taskCompletionRate.toFixed(1)}% (${completedTasks}/${totalTasks})

**Nylige aktiviteter/kommunikasjoner:**
${activitiesText || "Ingen aktiviteter"}

Beregn en score basert på:
1. Sentiment i kommunikasjoner (0-30 poeng)
2. Frekvens av interaksjoner (0-25 poeng)
3. Deal stage progresjon (0-25 poeng)
4. Oppgavefullføring (0-20 poeng)

Gi et JSON-svar med:
- score: number (0-100)
- breakdown: object med score for hver kategori
- insights: array av innsikter
- recommendations: array av anbefalinger for forbedring
- risk_level: string (low/medium/high)`,
        response_json_schema: {
          type: "object",
          properties: {
            score: { type: "number" },
            breakdown: {
              type: "object",
              properties: {
                sentiment: { type: "number" },
                interaction_frequency: { type: "number" },
                deal_progress: { type: "number" },
                task_completion: { type: "number" }
              }
            },
            insights: { type: "array", items: { type: "string" } },
            recommendations: { type: "array", items: { type: "string" } },
            risk_level: { type: "string", enum: ["low", "medium", "high"] }
          }
        }
      });

      // Update investor with new score
      await base44.entities.Investor.update(investor.id, {
        relationship_health_score: result.score,
        last_score_update: new Date().toISOString(),
      });

      queryClient.invalidateQueries({ queryKey: ["investors"] });
      toast.success(`Helsecore oppdatert: ${result.score}/100`);
      
      if (onScoreUpdated) {
        onScoreUpdated(result);
      }

      return result;
    } catch (error) {
      toast.error("Kunne ikke beregne helsecore");
      throw error;
    } finally {
      setCalculating(false);
    }
  };

  return (
    <Button
      onClick={calculateScore}
      disabled={calculating}
      size="sm"
      variant="outline"
    >
      {calculating ? (
        <>
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          Beregner...
        </>
      ) : (
        <>
          <RefreshCw className="w-4 h-4 mr-2" />
          Oppdater Score
        </>
      )}
    </Button>
  );
}